import React from 'react';
import ProductLineTitle from 'components/common/general/ProductLineTitle';
import { EnrollmentStage, EnrollmentType, InstalledProductLine, InstalledRate } from 'types/enrollmentService';
import { convertRateValue } from 'global/utils/convertRateValue';
import clsx from 'clsx';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { Download, InfoButton } from '@insurancemenu/imenu-components-lib/dist/icons';
import { Button } from '@insurancemenu/imenu-components-lib/dist/components';
import colors from '@insurancemenu/imenu-components-lib/dist/utils/colors';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { createStyles } from './styles';
import DateStatus from './DateStatus';
import { effectiveThrough } from '../../utils';

export interface ProductLineDataProps {
  productLineData: InstalledProductLine;
  maxRatesCount: number;
  hasMultiProductsPortfolio: boolean;
  effectiveDate: string;
  stage?: EnrollmentStage;
}

const InstalledPortfolioCard = observer(
  ({
    productLineData,
    maxRatesCount,
    hasMultiProductsPortfolio,
    effectiveDate,
    stage = EnrollmentStage.INSTALLED,
  }: ProductLineDataProps): JSX.Element => {
    const { type, id: productLineId, products, terminationEffectiveDate } = productLineData;
    const isTerminated = type === EnrollmentType.TERMINATED;
    const isPortfolioWithSeveralProducts = products.length > 1;
    const terminationDate = terminationEffectiveDate ? normDateToShortAmericanDate(terminationEffectiveDate) : '';

    const {
      enrollmentCardStore: { showBenefitSummariesDownload, downloadBenefitSummary },
    } = useRootStore();

    // rates list value description
    // maxRatesCount * 2 + 0.8 * (maxRatesCount - 1) + 1.5
    // total items quantity * height of each item + margin bottom for all items except last one * (total items - 1) + padding bottom
    const ratesBlockHeight = maxRatesCount
      ? `minmax(${maxRatesCount * 2 + 0.8 * (maxRatesCount - 1) + 1.5}rem, auto)`
      : 'minmax(max-content, 30%)';

    const classes = createStyles({
      isTerminated,
      productsLength: products.length,
      ratesBlockHeight, // property to keep similar height in single product portfolio
      hasMultiProductsPortfolio, // property to align single product portfolios content in all cards
      installedOnlyCard: stage === EnrollmentStage.INSTALLED,
    });

    const effectiveThroughDate = dateFormatters.normDateToShortAmericanDate(effectiveThrough(effectiveDate));

    const ratesItems = (imProductId: string, rates: InstalledRate[]): JSX.Element[] => {
      return rates.map(({ rateStructure, currentRate }) => {
        return (
          <li key={`${imProductId}-${rateStructure}`} className={classes.ratesItem}>
            <span>{rateStructure}</span>
            <span>{convertRateValue(currentRate)}</span>
          </li>
        );
      });
    };

    const benefitSummaryButton = (carrierProductId: string): JSX.Element => (
      <Button
        leftIconProp={<Download />}
        type="text"
        size="small"
        className={classes.downloadButton}
        color="primary"
        uppercase
        onClick={() => {
          downloadBenefitSummary(carrierProductId);
        }}
      >
        Download Benefit Summary
      </Button>
    );

    const CardHeader = (
      <div className={classes.productHeader}>
        <DateStatus
          type={isTerminated ? 'TERMINATED' : 'ACTIVE'}
          date={isTerminated ? terminationDate : effectiveThroughDate}
        />
        {stage === EnrollmentStage.INSTALLED && (
          <ProductLineTitle productLineId={productLineId} styles={classes.productLineTitle} />
        )}
      </div>
    );

    const productsList = products.map(({ carrierProductId, carrierProductName, rates }) => {
      const filteredRates = rates.filter(({ currentRate }) => currentRate && currentRate !== 'N/A');

      const RatesContent = (
        <div className={clsx(showBenefitSummariesDownload && classes.ratesContentWithButton)}>
          <p className={classes.ratesTitle}>Current Rates</p>
          <div className={classes.ratesWrapper}>
            {filteredRates.length > 0 ? (
              <ul className={classes.ratesList}>{ratesItems(carrierProductId, filteredRates)}</ul>
            ) : (
              <div className={classes.ratesMessage}>
                <InfoButton width="2rem" height="2rem" color={colors.red[600]} /> <span>Rates are unavailable</span>
              </div>
            )}
          </div>
        </div>
      );

      if (isPortfolioWithSeveralProducts) {
        return (
          <div key={`${carrierProductId}_${carrierProductName}`} className={classes.multiProductWrapper}>
            <h3 className={classes.productTitle}>{carrierProductName}</h3>
            {RatesContent}
            {showBenefitSummariesDownload && benefitSummaryButton(carrierProductId)}
          </div>
        );
      }

      return (
        <div key={`${carrierProductId}_${carrierProductName}`} className={classes.singleProductWrapper}>
          {CardHeader}
          <h3 className={clsx(classes.productTitle, classes.singleProductPortfolioTitle)}>{carrierProductName}</h3>
          {RatesContent}
          {showBenefitSummariesDownload && benefitSummaryButton(carrierProductId)}
        </div>
      );
    });

    return (
      <div className={classes.productCard}>
        {isPortfolioWithSeveralProducts ? (
          <>
            {CardHeader}
            <div className={classes.productsList}>{productsList}</div>
          </>
        ) : (
          productsList
        )}
      </div>
    );
  },
);

export default InstalledPortfolioCard;
